import { createPortal } from 'react-dom';
import { useRef, useEffect } from 'react';

import { useClickOutside } from 'hooks';

import * as Styles from './styles';
import { DrawerProps } from './types';
import { DrawerHeader } from './header';

export const Drawer = ({
  opened,
  onClose,
  children,
  elevated,
  topOffset,
  overModal,
  hideOverlay,
}: DrawerProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, onClose);

  // Esc closes an open drawer. The latest onClose is read from a ref so a
  // parent re-render never re-registers the listener or moves focus (no
  // auto-focus here: on POS tablets it would raise the keyboard).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onCloseRef.current) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened]);

  return createPortal(
    <>
      {!hideOverlay && <Styles.Overlay opened={opened} />}
      <Styles.RootStyles opened={opened} />
      <Styles.Container
        ref={ref}
        opened={opened}
        $elevated={elevated}
        topOffset={topOffset}
        overModal={overModal}
      >
        {children}
      </Styles.Container>
    </>,
    document.body
  );
};

export { DrawerHeader };
export type { DrawerProps };
